import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "../components/Seo";
import PageBanner from "../components/PageBanner";
import Reveal from "../components/Reveal";
import { CREDIT_CARD_BENEFITS } from "../data/site";

const SERVICES = [
  "Personalised card recommendations based on your spending profile",
  "Assistance with cashback, travel, fuel, lifestyle and premium cards",
  "End-to-end application support across multiple partner banks",
  "Guidance on eligibility, limits and reward optimisation",
];

export default function CreditCards() {
  return (
    <>
      <Seo
        title="Credit Card Assistance | Compare & Apply Across Banks – CARDED"
        description="Get expert credit card assistance from CARDED. Compare cashback, travel and lifestyle cards across multiple banks with easy documentation and quick processing."
      />
      <PageBanner
        overline="Credit Cards"
        title="Credit Card Assistance"
        subtitle="Find the right credit card for how you actually spend. We compare options across leading banks and support you from application to approval."
      />

      <section className="bg-white pb-8" data-testid="creditcard-intro">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 sm:px-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <h2 className="text-3xl font-medium tracking-tight text-[#09090B] sm:text-4xl">
              Services we offer.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-zinc-600">
              Choosing a credit card can be overwhelming. CARDED simplifies it — we understand
              your requirement, shortlist suitable cards and handle the paperwork so you can
              apply with confidence.
            </p>
            <ul className="mt-8 space-y-4">
              {SERVICES.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm text-zinc-700 sm:text-base">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full brand-gradient text-white">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {s}
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              className="mt-10 inline-flex items-center gap-2 rounded-full brand-gradient px-8 py-4 font-medium text-white transition-transform hover:scale-105 hover:shadow-[0_8px_24px_rgba(108,43,217,0.3)]"
              data-testid="creditcard-apply-top"
            >
              Apply Now <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>

          <Reveal direction="left" className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-3xl border border-[#E4E4E7]">
              <img
                src="https://images.pexels.com/photos/164501/pexels-photo-164501.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Premium credit card – CARDED credit card assistance"
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#6C2BD9]/50 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-24 sm:py-32" data-testid="creditcard-benefits">
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6C2BD9]">Benefits</span>
            <h2 className="mt-4 max-w-2xl text-4xl font-medium tracking-tight text-[#09090B] sm:text-5xl">
              Why apply through CARDED.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CREDIT_CARD_BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <div className="group h-full rounded-2xl border border-[#E4E4E7] bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6C2BD9]/10">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F4F4F5] text-[#6C2BD9] transition-colors group-hover:brand-gradient group-hover:text-white">
                    <b.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-lg font-semibold text-[#09090B]">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-500">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24 sm:px-10 sm:pb-32">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl brand-gradient px-8 py-16 text-center sm:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="mx-auto max-w-2xl text-3xl font-semibold tracking-tight text-white sm:text-5xl">
              Ready to find your card?
            </h2>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-10 py-4 font-medium text-[#6C2BD9] transition-transform hover:scale-105"
              data-testid="creditcard-apply-cta"
            >
              Apply Now <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
